import $ from 'jquery';
import { observable } from '@nx-js/observer-util';
import { ApplicationController } from '@aha-app/mvc';
import HasGlobalContainer, {
  type IHasGlobalContainer,
} from 'components/framework/controllers/concerns/HasGlobalContainer';
import SelectionAppContainer from './SelectionAppContainer';
import recordActionGroups from './models/commonActions';
import ExtensionAction from './models/ExtensionAction';
import AhaAnalytics from 'javascripts/analytics';
import { IS_MAC } from '@aha-app/is-hotkey';
import { type SelectableItemType } from 'javascripts/dnd/SelectableItem';
import type SelectableItem from 'javascripts/dnd/SelectableItem';
import { batch } from '@aha-app/react-easy-state';
import type RecordAction from './models/RecordAction';
import { type Extension } from 'components/framework/models';
import ApplicationModel from 'components/framework/ApplicationModel';
import { type RecordUnion } from 'components/framework/models/generated/types';

type CustomActionRender = () => JSX.Element;
type CustomActionRenderPosition = 'top' | 'bottom';

type ExtensionActions = {
  extension: Extension;
  actions: Array<ExtensionAction>;
};

type CommonActionGroup = {
  type: 'common';
  actions: Array<RecordAction>;
};

type ExtensionActionGroup = {
  type: 'extension';
  actions: Array<ExtensionActions>;
};

type ContextMenuGroup = CommonActionGroup | ExtensionActionGroup;

export type ContextMenuOptions = {
  actions?: string[]; // Allow-list of actions to show, defaults to all (if none specified)
  customActionRender?: CustomActionRender;
  customActionRenderPosition?: CustomActionRenderPosition;
  hideExtensionActions?: boolean;
};

type ContextMenuState = {
  open: boolean;
  x: number;
  y: number;
  actions: any[];
};

type SelectableModel = SelectableItem<ApplicationModel, any>;

type SelectionControllerProps = {};
type SelectionControllerState = {
  items: Map<string, SelectableModel>;
  contextMenu: ContextMenuState;
};

export function isModSelect(event: MouseEvent) {
  return (IS_MAC ? event.metaKey : event.ctrlKey) || event.shiftKey;
}

const recordTypeForAnalytics = records => {
  return records.every(r => r.typename === records[0].typename)
    ? records[0].typename
    : 'Mixed';
};

const emptyContextMenuState = (): ContextMenuState => ({
  open: false,
  x: 0,
  y: 0,
  actions: [],
});

export const createEmptyState = (): SelectionControllerState => ({
  items: new Map(),
  contextMenu: emptyContextMenuState(),
});

interface SelectionController extends IHasGlobalContainer {}

@HasGlobalContainer(SelectionAppContainer)
class SelectionController extends ApplicationController<
  SelectionControllerState,
  SelectionControllerProps
> {
  static initialState = createEmptyState();

  static openContextMenuForItems(
    items: SelectableModel[],
    x: number,
    y: number,
    options: ContextMenuOptions = {}
  ) {
    const controller = new SelectionController();
    controller.internalInitialize(null, {});
    items.forEach(item => controller.add(item));

    controller.openContextMenu(x, y, options);
  }

  customActionRender?: CustomActionRender;
  customActionRenderPosition?: CustomActionRenderPosition;

  async initialize() {
    this.reset();

    $(document).on('page:change', () => {
      this.reset();
    });

    document.addEventListener('click', this.globalClickHandler);
    document.addEventListener('keydown', this.globalKeyDownHandler);
  }

  unlisten() {
    document.removeEventListener('click', this.globalClickHandler);
    document.removeEventListener('keydown', this.globalKeyDownHandler);
  }

  canRunCommandForItem(command, item) {
    const { recordTypes } = command;

    if (recordTypes && recordTypes.length) {
      if (!recordTypes.includes(item.typename)) {
        return false;
      }
    }

    return true;
  }

  canRunCommand(command) {
    return this.selectedItems.every(item =>
      this.canRunCommandForItem(command, item)
    );
  }

  prepareActions(options: ContextMenuOptions = {}) {
    const actionGroups = this.prepareCommonActions(options);
    const extensionGroupIndex = actionGroups.findIndex(
      group => group.type === 'extension'
    );
    if (extensionGroupIndex > -1) {
      actionGroups[extensionGroupIndex].actions =
        this.prepareExtensionActions(options);
    }
    return actionGroups.filter(
      group => group.actions && group.actions.length > 0
    );
  }

  prepareCommonActions(options: ContextMenuOptions = {}) {
    const items = this.selectedItems.filter(
      item => item instanceof ApplicationModel
    );

    return recordActionGroups.map((group): ContextMenuGroup => {
      if (group.type === 'extension') return { type: 'extension', actions: [] };

      return {
        type: 'common',
        actions: group.actions
          .filter(ActionClass =>
            options.actions ? options.actions.includes(ActionClass.name) : true
          )
          .map(ActionClass => new ActionClass(items))
          .filter(action => action.showForSelectedRecords()),
      };
    });
  }

  prepareExtensionActions(options: ContextMenuOptions = {}) {
    if (options.hideExtensionActions) {
      return [];
    }

    const items = this.selectedItems;
    const recordActions = ((window.aha &&
      'recordActions' in window.aha &&
      // @ts-ignore
      window.aha.recordActions) ||
      {}) as Map<string, Map<string, RecordAction>>;

    return Object.keys(recordActions)
      .map((extensionIdentifier): ExtensionActions | undefined => {
        const extensionActions = recordActions[extensionIdentifier] as Map<
          string,
          RecordAction
        >;
        const extension = Object.values(extensionActions)[0]
          .extension as Extension;
        const actions = Object.values(extensionActions)
          .map(action => new ExtensionAction(items, action))
          .filter(action => items.some(item => action.showForRecord(item)))
          // Contributions are sorted on the server, but adding sort() here to make sure as
          // Object.keys does not guarantee order.
          .sort(
            (
              { command: { position: aPos } },
              { command: { position: bPos } }
            ) => {
              if (!aPos) return 1;
              if (!bPos) return -1;
              return aPos - bPos;
            }
          );

        if (actions.length) {
          return {
            extension,
            actions,
          };
        }
      })
      .filter(Boolean);
  }

  prepareContextMenu(options: ContextMenuOptions = {}) {
    if (this.empty) {
      return;
    }

    const actions = this.prepareActions(options);
    this.customActionRender = options.customActionRender;
    this.customActionRenderPosition = options.customActionRenderPosition;

    Object.assign(this.state.contextMenu, {
      actions,
    });
  }

  openContextMenu(x, y, options: ContextMenuOptions = {}) {
    if (this.empty) {
      return;
    }

    const actions = this.prepareActions(options);
    this.customActionRender = options.customActionRender;
    this.customActionRenderPosition =
      options.customActionRenderPosition ?? 'bottom';

    if (actions.length === 0 && !this.customActionRender) {
      return;
    }

    // Avoids a bug where the menu opens a second copy and throws a React error
    // if the click is within a pixel of the top or left of the existing menu
    if (this.state.contextMenu.open) {
      const prevMenu = document.getElementById('action-context-menu');
      if (prevMenu) {
        const { offsetLeft, offsetTop, offsetWidth, offsetHeight } = prevMenu;
        const leftDiff = x - offsetLeft;
        const topDiff = offsetTop - y;
        const clickToTop =
          [0, 1].includes(topDiff) && leftDiff >= 0 && leftDiff <= offsetWidth;
        const clickToLeft =
          [-1, 0].includes(leftDiff) &&
          topDiff <= 0 &&
          topDiff >= offsetHeight * -1;
        if (clickToTop || clickToLeft) {
          return;
        }
      }
    }

    Object.assign(this.state.contextMenu, {
      open: true,
      x,
      y,
      actions,
    });

    AhaAnalytics.track(
      'Feature-7006029182195823341',
      'User right clicks card',
      {
        'Record type': recordTypeForAnalytics(this.selectedItems),
      }
    );

    this.renderGlobalContainer();
  }

  closeContextMenu() {
    Object.assign(this.state.contextMenu, emptyContextMenuState());
  }

  reset() {
    const { state } = this;
    batch(() => {
      state.items.clear();
      state.contextMenu = emptyContextMenuState();
      this.dispatchAhaRecordSelectionChanged();
    });
  }

  get empty() {
    return this.state.items.size === 0;
  }

  get selectedItems() {
    return Array.from(this.state.items.values()).map(
      selectableItem => selectableItem.item
    );
  }

  get selectableItems() {
    return Array.from(this.state.items.values());
  }

  hasId(id: string | string[]) {
    const ids = Array.isArray(id) ? id : [id];
    for (const selectableItem of this.state.items.values()) {
      // @ts-ignore
      if (ids.includes(selectableItem.item.id)) {
        return true;
      }
    }
    return false;
  }

  has(selectableItem: SelectableModel) {
    return this.state.items.has(selectableItem.uniqueKey);
  }

  add(selectableItem: SelectableModel) {
    this.state.items.set(selectableItem.uniqueKey, selectableItem);
    this.dispatchAhaRecordSelectionChanged();
  }

  remove(selectableItem: SelectableModel) {
    this.state.items.delete(selectableItem.uniqueKey);
    this.dispatchAhaRecordSelectionChanged();
  }

  removeItem(item: SelectableItemType) {
    const selectableItem = this.selectableItems.find(
      selectableItem => selectableItem.item === item
    );

    if (selectableItem) {
      this.remove(selectableItem);
    }
  }

  set(items: SelectableModel[]) {
    this.state.items = observable(new Map());
    items.forEach(item => this.add(item));
    this.dispatchAhaRecordSelectionChanged();
    this.closeContextMenu();
  }

  private dispatchAhaRecordSelectionChanged() {
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('aha-record:selection:changed'));
    }, 0);
  }

  toggle(item: SelectableModel) {
    if (this.has(item)) {
      this.remove(item);
    } else {
      this.add(item);
    }
  }

  handleItemClick({
    item,
    event,
  }: {
    item: SelectableModel;
    event: MouseEvent;
  }) {
    if (isModSelect(event)) {
      // add/toggle to selection
      this.toggle(item);

      // prevent drawer from opening
      event.preventDefault();
      event.stopPropagation();
    } else {
      this.set([item]);
    }
  }

  globalClickHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    // By default, clicking on an "item" (a card, a page tree node, etc) will call `reset()` which will effectively clear the selection.
    // We want to prevent this from happening in certain cases.
    if (
      target.closest('.card') ||
      target.closest('aha-menu') ||
      isModSelect(event) ||
      // only clear selection if the click is in the main part of the app screen
      !target.closest('#workspace, #page-nav, nav.top-nav')
    ) {
      // clicking on a card, assuming selection will be handled by the appropriate UI
      return;
    }

    this.reset();
  };

  globalKeyDownHandler = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        this.closeContextMenu();
        break;
    }
  };

  handleContextMenu = ({
    event,
    item,
  }: {
    event: MouseEvent;
    item: SelectableModel;
  }) => {
    // if an item is passed in, and it's not in the selection, we reset the selection to the passed in item
    if (item && !this.has(item)) {
      if (isModSelect(event)) {
        this.add(item);
      } else {
        this.set([item]);
      }
    } else if (!item) {
      // No item, do nothing
      return;
    }

    // show the context menu
    this.openContextMenu(event.pageX, event.pageY);
    event.preventDefault();
    event.stopPropagation();
  };

  selectedItemsForAction(action) {
    const items = this.selectableItems;
    return items
      .filter(record => action.showForRecord(record.item))
      .map(record => Object.assign({}, record, { item: record.item.dup() }));
  }

  actionTriggerRecordAction(action, data?: {}) {
    const selectedItemsForAction = this.selectedItemsForAction(action);
    const recordsForAction = selectedItemsForAction.map(
      selectedItem => selectedItem.item
    );

    if (recordsForAction.length) {
      action.runForRecords(
        recordsForAction,
        data,
        selectedItemsForAction[0].controller
      );

      AhaAnalytics.track(
        'Feature-7006029182195823341',
        'User performs action via right-click menu',
        {
          'Record type': recordTypeForAnalytics(recordsForAction),
          'Records selected': recordsForAction.length,
          'Action name': action.title,
        }
      );
    }
  }

  async actionTriggerExtensionCommand(action) {
    const { command } = action;
    const items = this.selectedItems;
    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      await aha.command(command.identifier, {
        record: item.dup(),
        ahaCommand: {
          context: (item as RecordUnion).referenceNum,
          closeAfterCommand: index === items.length - 1,
        },
      });
    }

    AhaAnalytics.track(
      'Feature-7006029182195823341',
      'User performs action via right-click menu',
      {
        'Record type': recordTypeForAnalytics(items),
        'Records selected': items.length,
        'Action name': command.title,
        'Extension ID': action.extension.trusted
          ? action.extension.identifier
          : undefined,
      }
    );
  }
}

export const sharedSelectionController = new SelectionController();
sharedSelectionController.internalInitialize(null, {});

export default SelectionController;
